/**
 * Bon-Einstellungen - Receipt Settings Page
 * 
 * Features:
 * - Drucker-Modell Auswahl mit Auto-Erkennung
 * - Logo-Upload
 * - Schriftart-Auswahl
 * - Schriftgrößen für verschiedene Bereiche
 * - Live-Vorschau
 * - Testdruck
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Printer, Upload, Type, Sliders, Eye, Save, 
  RefreshCw, ArrowLeft, Check, X, Image,
  Settings, FileText, Trash2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Font Size Labels
const FONT_SIZE_LABELS = {
  6: '6pt (sehr klein)',
  8: '8pt (klein)',
  10: '10pt (normal-klein)',
  12: '12pt (normal)',
  14: '14pt (groß)',
  16: '16pt (sehr groß)',
  18: '18pt (extra groß)',
  20: '20pt (XXL)',
  24: '24pt (Überschrift)',
  32: '32pt (Titel)'
};

export default function BonEinstellungen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  
  // Daten
  const [printerModels, setPrinterModels] = useState([]);
  const [groupedModels, setGroupedModels] = useState({});
  const [fonts, setFonts] = useState([]);
  const [accountData, setAccountData] = useState({});
  
  // Einstellungen
  const [settings, setSettings] = useState({
    printer: {
      printer_model_id: 'generic_80mm',
      paper_width: 80,
      chars_font_a: 48,
      chars_font_b: 64,
      auto_detected: false
    },
    layout: {
      font_family: 'arial',
      header_font: { size: 8, bold: false, italic: false },
      customer_font: { size: 14, bold: true, italic: false },
      order_info_font: { size: 14, bold: true, italic: false },
      items_font: { size: 12, bold: false, italic: false },
      totals_font: { size: 14, bold: true, italic: false },
      mwst_font: { size: 8, bold: false, italic: false },
      tse_font: { size: 8, bold: false, italic: false },
      footer_font: { size: 12, bold: false, italic: false },
      scheduled_time_inverted: true,
      special_requests_inverted: true,
      removed_items_inverted: true,
      section_spacing: 1
    },
    company: {
      use_account_data: true,
      business_name: '',
      owner_name: '',
      address: '',
      city: '',
      phone: '',
      tax_number: '',
      website: ''
    },
    logo_enabled: true,
    logo_position: 'center',
    logo_max_width: 300,
    print_qr_customer: false,
    print_qr_tse: true,
    show_tax_details: true,
    show_tse_data: true,
    footer_message: 'Vielen Dank für Ihre Bestellung!\nGuten Appetit!'
  });
  
  const [logoUrl, setLogoUrl] = useState(null);
  const [preview, setPreview] = useState('');
  
  // Session Token laden
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login');
      return;
    }
    setSessionToken(token);
  }, [navigate]);
  
  // Daten laden
  useEffect(() => {
    if (!sessionToken) return;
    
    const loadData = async () => {
      setLoading(true);
      try {
        // Drucker-Modelle laden
        const modelsRes = await fetch(`${BACKEND_URL}/api/receipt-settings/printer-models`);
        const modelsData = await modelsRes.json();
        setPrinterModels(modelsData.models || []);
        setGroupedModels(modelsData.grouped || {});
        
        // Schriftarten laden
        const fontsRes = await fetch(`${BACKEND_URL}/api/receipt-settings/fonts`);
        const fontsData = await fontsRes.json();
        setFonts(fontsData.fonts || []);
        
        // Aktuelle Einstellungen laden
        const settingsRes = await fetch(`${BACKEND_URL}/api/receipt-settings/`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          if (data.settings) {
            setSettings(prev => ({ ...prev, ...data.settings }));
          }
          if (data.logo_url) {
            setLogoUrl(data.logo_url);
          }
          if (data.account) {
            setAccountData(data.account);
          }
        }
        
        // Vorschau laden
        await loadPreview();
        
      } catch (error) {
        console.error('Fehler beim Laden:', error);
        toast.error('Fehler beim Laden der Einstellungen');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [sessionToken]);
  
  // Vorschau laden
  const loadPreview = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/receipt-settings/preview`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPreview(data.preview || '');
      }
    } catch (error) {
      console.error('Vorschau-Fehler:', error);
    }
  }, [sessionToken]);
  
  // Einstellungen speichern
  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/receipt-settings/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Einstellungen gespeichert');
        await loadPreview();
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Speicher-Fehler:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };
  
  // Logo hochladen
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validierung
    if (!file.type.startsWith('image/')) {
      toast.error('Nur Bilddateien erlaubt');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Datei zu groß (max 2MB)');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/receipt-settings/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data.logo_url);
        toast.success('Logo hochgeladen');
      } else {
        toast.error('Fehler beim Hochladen');
      }
    } catch (error) {
      console.error('Upload-Fehler:', error);
      toast.error('Fehler beim Hochladen');
    }
  };
  
  // Logo löschen
  const deleteLogo = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/receipt-settings/logo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (res.ok) {
        setLogoUrl(null);
        toast.success('Logo gelöscht');
      }
    } catch (error) {
      console.error('Lösch-Fehler:', error);
    }
  };
  
  // Drucker-Modell ändern
  const handlePrinterModelChange = (modelId) => {
    const model = printerModels.find(m => m.id === modelId);
    if (!model) return;
    
    // Spezifikationen vom Backend laden
    fetch(`${BACKEND_URL}/api/receipt-settings/printer-model/${modelId}`)
      .then(res => res.json())
      .then(data => {
        const paperWidth = data.default_paper_width;
        const specs = data.specs[paperWidth];
        
        setSettings(prev => ({
          ...prev,
          printer: {
            ...prev.printer,
            printer_model_id: modelId,
            paper_width: paperWidth,
            chars_font_a: specs.font_a,
            chars_font_b: specs.font_b
          }
        }));
      })
      .catch(console.error);
  };
  
  // Testdruck
  const triggerTestPrint = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/receipt-settings/test-print`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || 'Testdruck ausgelöst');
      } else {
        toast.error(data.detail || 'Fehler beim Testdruck');
      }
    } catch (error) {
      console.error('Testdruck-Fehler:', error);
      toast.error('Fehler beim Testdruck');
    }
  };
  
  // Font-Einstellung Komponente
  const FontSizeControl = ({ label, settingKey, description }) => {
    const fontSetting = settings.layout[settingKey] || { size: 12, bold: false };
    
    return (
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <div>
            <Label className="font-medium">{label}</Label>
            {description && (
              <p className="text-xs text-gray-500">{description}</p>
            )}
          </div>
          <span className="text-sm font-mono bg-white px-2 py-1 rounded border">
            {fontSetting.size}pt
          </span>
        </div>
        
        <Slider
          value={[fontSetting.size]}
          onValueChange={([value]) => {
            setSettings(prev => ({
              ...prev,
              layout: {
                ...prev.layout,
                [settingKey]: { ...fontSetting, size: value }
              }
            }));
          }}
          min={6}
          max={32}
          step={2}
          className="w-full"
        />
        
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={fontSetting.bold}
              onCheckedChange={(checked) => {
                setSettings(prev => ({
                  ...prev,
                  layout: {
                    ...prev.layout,
                    [settingKey]: { ...fontSetting, bold: checked }
                  }
                }));
              }}
            />
            <span className="text-sm font-bold">Fett</span>
          </label>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p>Lade Einstellungen...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Bon-Einstellungen
              </h1>
              <p className="text-sm text-gray-500">
                Drucker, Logo, Schriftarten und Layout anpassen
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={triggerTestPrint}
            >
              <Printer className="w-4 h-4 mr-2" />
              Testdruck
            </Button>
            <Button
              onClick={saveSettings}
              disabled={saving}
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Speichern
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Linke Spalte - Einstellungen */}
          <div className="lg:col-span-2 space-y-6">
            
            <Tabs defaultValue="printer" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="printer">
                  <Printer className="w-4 h-4 mr-2" />
                  Drucker
                </TabsTrigger>
                <TabsTrigger value="logo">
                  <Image className="w-4 h-4 mr-2" />
                  Logo
                </TabsTrigger>
                <TabsTrigger value="fonts">
                  <Type className="w-4 h-4 mr-2" />
                  Schriften
                </TabsTrigger>
                <TabsTrigger value="layout">
                  <Sliders className="w-4 h-4 mr-2" />
                  Layout
                </TabsTrigger>
              </TabsList>
              
              {/* Drucker Tab */}
              <TabsContent value="printer">
                <Card>
                  <CardHeader>
                    <CardTitle>Drucker-Konfiguration</CardTitle>
                    <CardDescription>
                      Wählen Sie Ihr Druckermodell für optimale Ergebnisse
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    
                    {/* Drucker-Modell */}
                    <div className="space-y-2">
                      <Label>Drucker-Modell</Label>
                      <Select
                        value={settings.printer.printer_model_id}
                        onValueChange={handlePrinterModelChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Drucker auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(groupedModels).map(([manufacturer, models]) => (
                            <React.Fragment key={manufacturer}>
                              <div className="px-2 py-1.5 text-sm font-semibold text-gray-500 bg-gray-50">
                                {manufacturer}
                              </div>
                              {models.map(model => (
                                <SelectItem key={model.id} value={model.id}>
                                  {model.display_name}
                                </SelectItem>
                              ))}
                            </React.Fragment>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Papierbreite */}
                    <div className="space-y-2">
                      <Label>Papierbreite</Label>
                      <Select
                        value={String(settings.printer.paper_width)}
                        onValueChange={(value) => {
                          setSettings(prev => ({
                            ...prev,
                            printer: { ...prev.printer, paper_width: parseInt(value) }
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="58">58mm</SelectItem>
                          <SelectItem value="80">80mm</SelectItem>
                          <SelectItem value="112">112mm</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Zeichen pro Zeile */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Zeichen Font A (normal)</Label>
                        <Input
                          type="number"
                          value={settings.printer.chars_font_a}
                          onChange={(e) => {
                            setSettings(prev => ({
                              ...prev,
                              printer: { ...prev.printer, chars_font_a: parseInt(e.target.value) || 48 }
                            }));
                          }}
                          min={20}
                          max={100}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Zeichen Font B (klein)</Label>
                        <Input
                          type="number"
                          value={settings.printer.chars_font_b}
                          onChange={(e) => {
                            setSettings(prev => ({
                              ...prev,
                              printer: { ...prev.printer, chars_font_b: parseInt(e.target.value) || 64 }
                            }));
                          }}
                          min={20}
                          max={100}
                        />
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Tipp:</strong> Wenn Sie unsicher sind, führen Sie einen 
                        Testdruck durch. Die Zeilen-Nummern helfen Ihnen, die korrekte 
                        Zeichenanzahl für Ihren Drucker zu ermitteln.
                      </p>
                    </div>
                    
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Logo Tab */}
              <TabsContent value="logo">
                <Card>
                  <CardHeader>
                    <CardTitle>Logo</CardTitle>
                    <CardDescription>
                      Laden Sie Ihr Firmenlogo hoch (PNG oder JPG, max 2MB)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    
                    {/* Logo Vorschau */}
                    <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg border-2 border-dashed">
                      {logoUrl ? (
                        <div className="text-center">
                          <img 
                            src={logoUrl} 
                            alt="Logo" 
                            className="max-h-32 max-w-xs mx-auto mb-4"
                          />
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={deleteLogo}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Logo löschen
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500 mb-4">Kein Logo hochgeladen</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Upload Button */}
                    <div>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <Label htmlFor="logo-upload" className="cursor-pointer">
                        <Button variant="outline" asChild>
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Logo hochladen
                          </span>
                        </Button>
                      </Label>
                    </div>
                    
                    {/* Logo Optionen */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label>Logo auf Bon drucken</Label>
                        <Switch
                          checked={settings.logo_enabled}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({ ...prev, logo_enabled: checked }));
                          }}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Logo-Position</Label>
                        <Select
                          value={settings.logo_position}
                          onValueChange={(value) => {
                            setSettings(prev => ({ ...prev, logo_position: value }));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Links</SelectItem>
                            <SelectItem value="center">Zentriert</SelectItem>
                            <SelectItem value="right">Rechts</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Maximale Breite: {settings.logo_max_width}px</Label>
                        <Slider
                          value={[settings.logo_max_width]}
                          onValueChange={([value]) => {
                            setSettings(prev => ({ ...prev, logo_max_width: value }));
                          }}
                          min={100}
                          max={500}
                          step={20}
                        />
                      </div>
                    </div>
                    
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Schriften Tab */}
              <TabsContent value="fonts">
                <Card>
                  <CardHeader>
                    <CardTitle>Schrifteinstellungen</CardTitle>
                    <CardDescription>
                      Wählen Sie Schriftart und -größen für jeden Bereich
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    
                    {/* Schriftart */}
                    <div className="space-y-2">
                      <Label>Schriftart</Label>
                      <Select
                        value={settings.layout.font_family}
                        onValueChange={(value) => {
                          setSettings(prev => ({
                            ...prev,
                            layout: { ...prev.layout, font_family: value }
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fonts.map(font => (
                            <SelectItem key={font.id} value={font.id}>
                              <span className="flex items-center gap-2">
                                <span style={{ fontFamily: font.name }}>{font.name}</span>
                                <span className="text-xs text-gray-400">({font.type})</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Schriftgrößen */}
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="header">
                        <AccordionTrigger>Header (Firmeninfos)</AccordionTrigger>
                        <AccordionContent>
                          <FontSizeControl 
                            label="Header-Schriftgröße" 
                            settingKey="header_font"
                            description="Firmenname, Adresse, Steuernummer"
                          />
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="customer">
                        <AccordionTrigger>Kundendaten</AccordionTrigger>
                        <AccordionContent>
                          <FontSizeControl 
                            label="Kunden-Schriftgröße" 
                            settingKey="customer_font"
                            description="Name, Adresse, Telefon"
                          />
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="order">
                        <AccordionTrigger>Auftragsinfo</AccordionTrigger>
                        <AccordionContent>
                          <FontSizeControl 
                            label="Auftrags-Schriftgröße" 
                            settingKey="order_info_font"
                            description="Uhrzeit, Auftragsnummer"
                          />
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="items">
                        <AccordionTrigger>Artikel</AccordionTrigger>
                        <AccordionContent>
                          <FontSizeControl 
                            label="Artikel-Schriftgröße" 
                            settingKey="items_font"
                            description="Artikelliste, Extras, Optionen"
                          />
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="totals">
                        <AccordionTrigger>Summen</AccordionTrigger>
                        <AccordionContent>
                          <FontSizeControl 
                            label="Summen-Schriftgröße" 
                            settingKey="totals_font"
                            description="Gesamtbetrag, Bar/Karte"
                          />
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="mwst">
                        <AccordionTrigger>MwSt-Tabelle</AccordionTrigger>
                        <AccordionContent>
                          <FontSizeControl 
                            label="MwSt-Schriftgröße" 
                            settingKey="mwst_font"
                            description="Steuer-Aufschlüsselung"
                          />
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="tse">
                        <AccordionTrigger>TSE-Daten</AccordionTrigger>
                        <AccordionContent>
                          <FontSizeControl 
                            label="TSE-Schriftgröße" 
                            settingKey="tse_font"
                            description="Signatur, Transaktionsnummer"
                          />
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="footer">
                        <AccordionTrigger>Footer</AccordionTrigger>
                        <AccordionContent>
                          <FontSizeControl 
                            label="Footer-Schriftgröße" 
                            settingKey="footer_font"
                            description="Danke-Text, Website"
                          />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Layout Tab */}
              <TabsContent value="layout">
                <Card>
                  <CardHeader>
                    <CardTitle>Layout-Optionen</CardTitle>
                    <CardDescription>
                      Hervorhebungen und zusätzliche Optionen
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    
                    {/* Hervorhebungen */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Hervorhebungen (Invertiert)</h3>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Zeitbestellung invertiert</Label>
                          <p className="text-xs text-gray-500">z.B. "LIEFERN UM: 19:30 UHR"</p>
                        </div>
                        <Switch
                          checked={settings.layout.scheduled_time_inverted}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({
                              ...prev,
                              layout: { ...prev.layout, scheduled_time_inverted: checked }
                            }));
                          }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Sonderwünsche invertiert</Label>
                          <p className="text-xs text-gray-500">z.B. "BITTE SEHR SCHARF!"</p>
                        </div>
                        <Switch
                          checked={settings.layout.special_requests_inverted}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({
                              ...prev,
                              layout: { ...prev.layout, special_requests_inverted: checked }
                            }));
                          }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>OHNE-Angaben invertiert</Label>
                          <p className="text-xs text-gray-500">z.B. "OHNE Zwiebeln"</p>
                        </div>
                        <Switch
                          checked={settings.layout.removed_items_inverted}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({
                              ...prev,
                              layout: { ...prev.layout, removed_items_inverted: checked }
                            }));
                          }}
                        />
                      </div>
                    </div>
                    
                    <hr />
                    
                    {/* Anzeigeoptionen */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Anzeige-Optionen</h3>
                      
                      <div className="flex items-center justify-between">
                        <Label>MwSt-Tabelle anzeigen</Label>
                        <Switch
                          checked={settings.show_tax_details}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({ ...prev, show_tax_details: checked }));
                          }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label>TSE-Daten anzeigen</Label>
                        <Switch
                          checked={settings.show_tse_data}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({ ...prev, show_tse_data: checked }));
                          }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label>QR-Code für TSE</Label>
                        <Switch
                          checked={settings.print_qr_tse}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({ ...prev, print_qr_tse: checked }));
                          }}
                        />
                      </div>
                    </div>
                    
                    <hr />
                    
                    {/* Footer Text */}
                    <div className="space-y-2">
                      <Label>Footer-Text</Label>
                      <Textarea
                        value={settings.footer_message}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, footer_message: e.target.value }));
                        }}
                        placeholder="Vielen Dank für Ihre Bestellung!"
                        rows={3}
                      />
                    </div>
                    
                  </CardContent>
                </Card>
              </TabsContent>
              
            </Tabs>
          </div>
          
          {/* Rechte Spalte - Vorschau */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Live-Vorschau
                </CardTitle>
                <CardDescription>
                  So wird Ihr Bon aussehen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-white border rounded-lg p-4 font-mono text-xs overflow-x-auto">
                  <pre className="whitespace-pre-wrap">{preview || 'Vorschau wird geladen...'}</pre>
                </div>
                
                <div className="mt-4 flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={loadPreview}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Aktualisieren
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1"
                    onClick={triggerTestPrint}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Drucken
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          
        </div>
      </main>
    </div>
  );
}
